import { FileText, Menu, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PERMISSIONS } from '@virtuetasks/types';
import { useCan } from '@/features/auth';
import { useUiStore } from '@/stores/ui';
import { NotificationPanel } from '@/features/notifications';
import { DayChip } from '@/features/attendance';
import { AccountMenu } from './AccountMenu';

export function TopBar() {
  const setPalette = useUiStore((s) => s.setCommandPaletteOpen);
  const setQuickAdd = useUiStore((s) => s.setQuickAddOpen);
  const setNewTask = useUiStore((s) => s.setNewTaskOpen);
  const setMobileNavOpen = useUiStore((s) => s.setMobileNavOpen);
  const mobileNavOpen = useUiStore((s) => s.mobileNavOpen);
  const canCreateTask = useCan()(PERMISSIONS.TASK_CREATE);

  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b border-[var(--border)] bg-[var(--surface)] px-3">
      <Button
        variant="ghost"
        size="icon-sm"
        /**
         * V8-03 §2 — a 44px touch target on a phone.
         *
         * The audit this document asked for found the topbar's icon controls
         * rendering at `h-7 w-7` — 28px, and the hit area measured smaller
         * still. Both Apple and Google put the minimum at 44px, and a
         * hamburger somebody's thumb misses is indistinguishable from one that
         * does not work, which is very close to the report that started this.
         * Desktop keeps the compact size, where the pointer is a mouse.
         */
        // `min-*` rather than `h-11 w-11`: the variant already sets `h-7 w-7`,
        // and tailwind-merge resolved the height but dropped the width, giving
        // a 14×44 target. A minimum does not conflict with a width at all, so
        // there is nothing to resolve — plus `shrink-0`, because the topbar is
        // a flex row and was squeezing the minimum back down to 30px.
        className="min-h-11 min-w-11 shrink-0 md:min-h-0 md:min-w-0 md:hidden"
        data-testid="mobile-nav-toggle"
        aria-label="Toggle navigation"
        aria-expanded={mobileNavOpen}
        aria-controls="primary-navigation"
        /**
         * V8-03 §2 — this called `toggleSidebar`, which flips the *desktop*
         * collapse width on an element that is `display:none` below `md`. The
         * button was live, the handler ran, and nothing could possibly appear.
         */
        onClick={() => setMobileNavOpen(!mobileNavOpen)}
      >
        <Menu />
      </Button>

      <div className="flex select-none items-center gap-2 pr-2">
        <span
          className="grid size-6 place-items-center rounded-[6px] text-[13px] font-bold text-[var(--accent-contrast)]"
          style={{ background: 'var(--accent)' }}
          aria-hidden
        >
          V
        </span>
        <span className="hidden text-[14px] font-semibold sm:inline">VirtueTasks</span>
      </div>

      <button
        type="button"
        onClick={() => setPalette(true)}
        className="flex h-8 max-w-[420px] flex-1 items-center gap-2 rounded-[6px] border border-[var(--border)] bg-[var(--bg)] px-2.5 text-left text-[13px] text-[var(--text-muted)] transition-colors duration-[120ms] hover:border-[var(--accent)]"
      >
        <Search className="size-3.5" />
        <span className="truncate">Search tasks, notes, people…</span>
        <kbd className="vt-mono ml-auto hidden rounded-[4px] border border-[var(--border)] px-1 text-[10px] sm:inline">
          ⌘K
        </kbd>
      </button>

      {/**
       * V8-03 §2 — the touch minimum for every control in this row.
       *
       * The audit found the hamburger at 28px and these two the same: below
       * `sm` and `lg` their labels are hidden and they collapse to a bare
       * icon, so on a phone they are exactly the class of control §2 asked
       * about. 44px is the guidance Apple and Google both give.
       *
       * Applied to the row rather than to each button so a control added here
       * later inherits it — the failure mode is somebody adding the fourth
       * icon and nobody measuring it.
       */}
      <div className="ml-auto flex items-center gap-1 [&>*>button]:min-h-11 [&>*>button]:min-w-11 [&>button]:min-h-11 [&>button]:min-w-11 md:[&>*>button]:min-h-0 md:[&>*>button]:min-w-0 md:[&>button]:min-h-0 md:[&>button]:min-w-0">
        {/* Both creation paths need `task.create`; a guest holds none (§3.1). */}
        {canCreateTask && (
          <>
            <Button
              variant="primary"
              size="sm"
              data-testid="quick-add-button"
              onClick={() => setQuickAdd(true)}
            >
              <Plus />
              <span className="hidden sm:inline">Quick add</span>
            </Button>

            {/**
             * V3 R3: the complete path, beside the fast one — but not on a
             * phone.
             *
             * V8-03 §2's touch minimum widened every control in this row, and
             * at 360px the row then overflowed and pushed this button off the
             * screen entirely: present in the DOM, outside the viewport, and
             * untappable. Quick add already covers the same need in less
             * space, so the redundant one steps aside where there is no room
             * for it rather than being there and unreachable.
             */}
            <Button
              variant="subtle"
              size="sm"
              className="hidden sm:inline-flex"
              data-testid="new-task-button"
              onClick={() => setNewTask(true)}
              title="New task (⌘⇧N)"
            >
              <FileText />
              <span className="hidden lg:inline">New task</span>
            </Button>
          </>
        )}

        {/* V6-04 §4 — the whole daily interaction, one click, every page. */}
        <DayChip />

        <NotificationPanel />

        <AccountMenu />
      </div>
    </header>
  );
}
